/**
 * RenovationProjectsListPage
 *
 * Lists all renovation projects across all buildings.
 * Uses UniversalTable with manual pagination controls for optimal performance.
 * Displays project title, building, status, number of parts, and creation date.
 * Supports expandable rows to show project parts.
 */

import { memo, useCallback, useEffect, useState, useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Wall,
  Buildings,
  Plus,
  Eye,
  ArrowDown,
  ArrowsClockwise,
  Spinner,
  CaretDown,
  CaretRight,
  Tag,
} from "@phosphor-icons/react";
import { Button } from "components/ui/button";
import { API_URL } from "constants/index";
import { UniversalTable, useLazyLoading } from "shared/components/UniversalTable";
import { FilterBar } from "shared/components/filterBar";
import type {
  FilterSection,
  FilterChip,
} from "shared/components/filterBar/types";
import {
  RENOVATION_PROJECTS_TABLE_CONFIG,
  RENOVATION_PROJECTS_FILTER_SECTIONS,
} from "./configs/renovationProjectsTableConfig";
import { UniversalPickerModal } from "shared/components/UniversalPickerModal";
import { NewRenovationProjectModal } from "screens/renovationProject/NewRenovationProjectModal";

// Project status enum matching backend
enum ProjectStatus {
  DESIGN = "Ontwerp fase",
  PLANNED = "Gepland",
  IN_PROGRESS = "In uitvoering",
  COMPLETED = "Afgerond",
  PAID = "Volledig gefactureerd",
}

// Part status enum matching backend
enum PartStatus {
  DESIGN = "Ontwerp",
  PLANNED = "Gepland",
  IN_PROGRESS = "In uitvoering",
  COMPLETED = "Afgerond",
}

interface ProjectPart {
  id: number;
  title: string;
  status: PartStatus;
  label?: {
    id: number;
    name: string;
    color?: string;
  };
}

interface RenovationProject {
  id: number;
  title: string;
  description?: string;
  status: ProjectStatus;
  building?: {
    id: string;
    company?: { name?: string };
  };
  parts?: ProjectPart[];
  responsibleUserId?: string;
  createdAt: string;
}

// Status badge colors for projects
const getStatusColor = (status: ProjectStatus) => {
  switch (status) {
    case ProjectStatus.DESIGN:
      return "bg-blue-100 text-blue-700";
    case ProjectStatus.PLANNED:
      return "bg-yellow-100 text-yellow-700";
    case ProjectStatus.IN_PROGRESS:
      return "bg-orange-100 text-orange-700";
    case ProjectStatus.COMPLETED:
      return "bg-green-100 text-green-700";
    case ProjectStatus.PAID:
      return "bg-purple-100 text-purple-700";
    default:
      return "bg-gray-100 text-gray-700";
  }
};

// Status badge colors for parts
const getPartStatusColor = (status: PartStatus) => {
  switch (status) {
    case PartStatus.DESIGN:
      return "bg-blue-50 text-blue-600";
    case PartStatus.PLANNED:
      return "bg-yellow-50 text-yellow-600";
    case PartStatus.IN_PROGRESS:
      return "bg-orange-50 text-orange-600";
    case PartStatus.COMPLETED:
      return "bg-green-50 text-green-600";
    default:
      return "bg-gray-50 text-gray-600";
  }
};

const RenovationProjectsListPageComponent = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Filter state
  const [searchText, setSearchText] = useState("");
  const [statusFilters, setStatusFilters] = useState<string[]>([]);

  // Expanded rows state
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

  // "Nieuw project" flow: pick the building first (no buildingId in this
  // cross-building list view), then open the create modal scoped to it.
  const [buildingPickerOpen, setBuildingPickerOpen] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [pickedBuildingId, setPickedBuildingId] = useState<string | null>(
    null,
  );

  // Sidebar deep-link `?new=1` — auto-open the building picker once, then
  // strip the param so refreshes don't reopen the dialog.
  useEffect(() => {
    if (searchParams.get("new") === "1") {
      setBuildingPickerOpen(true);
      const next = new URLSearchParams(searchParams);
      next.delete("new");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Load all state
  const [isLoadingAll, setIsLoadingAll] = useState(false);
  const loadAllAbortRef = useRef<boolean>(false);
  const hasMoreRef = useRef<boolean>(true);

  // Toggle row expansion
  const toggleRowExpansion = useCallback((projectId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(projectId)) {
        newSet.delete(projectId);
      } else {
        newSet.add(projectId);
      }
      return newSet;
    });
  }, []);

  // Build filter sections for FilterBar
  const filterSections: FilterSection[] = useMemo(
    () => [
      {
        key: "status",
        label: "Status",
        options: RENOVATION_PROJECTS_FILTER_SECTIONS[0].options,
        selectedValues: statusFilters,
        onUpdate: (values: string[]) => setStatusFilters(values),
        color: "moss" as const,
      },
    ],
    [statusFilters],
  );

  // Build filter chips for display
  const filterChips: FilterChip[] = useMemo(() => {
    const chips: FilterChip[] = [];

    statusFilters.forEach((status) => {
      const option = RENOVATION_PROJECTS_FILTER_SECTIONS[0].options.find(
        (opt) => opt.key === status,
      );
      if (option) {
        chips.push({
          key: `status-${status}`,
          label: option.label,
          category: "Status",
          color: "moss",
          onRemove: () => {
            setStatusFilters((prev) => prev.filter((s) => s !== status));
          },
        });
      }
    });

    return chips;
  }, [statusFilters]);

  // Build additional params for the lazy loading hook
  const additionalParams = useMemo(() => {
    const params: Record<string, string> = {};
    if (searchText.trim()) {
      params.searchText = searchText.trim();
    }
    if (statusFilters.length > 0) {
      params.status = statusFilters.join(",");
    }
    return params;
  }, [searchText, statusFilters]);

  // Lazy loading hook for paginated data (without auto-fetch on scroll)
  const {
    data: projects,
    isLoading,
    isLoadingMore,
    hasMore,
    totalCount,
    fetch,
  } = useLazyLoading<RenovationProject>({
    endpoint: `${API_URL}/renovation/projects/paginated`,
    pageSize: 50,
    sortField: "createdAt",
    sortDirection: "DESC",
    additionalParams,
  });

  // Keep ref in sync with hasMore state
  hasMoreRef.current = hasMore;

  // Load next batch manually
  const handleLoadMore = useCallback(() => {
    if (!isLoadingMore && hasMore) {
      fetch(true); // append = true
    }
  }, [fetch, isLoadingMore, hasMore]);

  // Load all projects in batches
  const handleLoadAll = useCallback(async () => {
    if (isLoadingAll || !hasMoreRef.current) return;

    setIsLoadingAll(true);
    loadAllAbortRef.current = false;

    try {
      // Keep fetching until we have all data or user cancels
      // Use ref to get latest hasMore value in each iteration
      while (hasMoreRef.current && !loadAllAbortRef.current) {
        await fetch(true); // append = true
        // Small delay to allow state to update and prevent overwhelming the server
        await new Promise((resolve) => setTimeout(resolve, 150));
      }
    } catch (error) {
      console.error("Error loading all projects:", error);
    } finally {
      setIsLoadingAll(false);
    }
  }, [fetch, isLoadingAll]);

  // Cancel load all
  const handleCancelLoadAll = useCallback(() => {
    loadAllAbortRef.current = true;
  }, []);

  // Navigate to project detail
  const handleProjectClick = useCallback(
    (project: RenovationProject) => {
      if (project.building?.id) {
        navigate(
          `/building/${project.building.id}/renovatie?project=${project.id}`,
        );
      }
    },
    [navigate],
  );

  // Render expanded content for a project (shows its parts)
  const renderExpandedContent = useCallback(
    (project: RenovationProject) => {
      if (!project.parts || project.parts.length === 0) return null;

      return (
        <div className="bg-gray-50 border-l-4 border-moss-primary mx-4 mb-2 rounded-r-lg overflow-hidden">
          <div className="px-4 py-2 bg-gray-100 border-b border-gray-200">
            <span className="text-xs font-semibold text-gray-600 uppercase">
              Projectdelen ({project.parts.length})
            </span>
          </div>
          <div className="divide-y divide-gray-200">
            {project.parts.map((part) => (
              <div
                key={part.id}
                className="px-4 py-3 flex items-center gap-4 hover:bg-gray-100 transition-colors"
              >
                <span className="font-mono text-xs text-gray-500 w-16">
                  DEEL-{part.id}
                </span>
                <span className="flex-1 text-sm text-gray-900">
                  {part.title}
                </span>
                {part.label && (
                  <span
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs"
                    style={{
                      backgroundColor: part.label.color
                        ? `${part.label.color}20`
                        : "#f3f4f6",
                      color: part.label.color || "#6b7280",
                    }}
                  >
                    <Tag className="h-3 w-3" />
                    {part.label.name}
                  </span>
                )}
                <span
                  className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getPartStatusColor(part.status)}`}
                >
                  {part.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      );
    },
    [],
  );

  // Render row for UniversalTable
  const renderRow = useCallback(
    (
      project: RenovationProject,
      _context: {
        isSelected: boolean;
        isBulkActive: boolean;
        templateColumns: string;
        onToggleSelect: (selected: boolean) => void;
        onClearSelection: () => void;
      },
    ) => {
      const isExpanded = expandedRows.has(project.id);
      const hasParts = project.parts && project.parts.length > 0;

      return [
        // Code column with expand toggle
        {
          id: "code",
          content: (
            <div className="flex items-center gap-2">
              {hasParts ? (
                <button
                  onClick={(e) => toggleRowExpansion(project.id, e)}
                  className="p-0.5 hover:bg-gray-100 rounded transition-colors"
                >
                  {isExpanded ? (
                    <CaretDown className="h-4 w-4 text-gray-500" />
                  ) : (
                    <CaretRight className="h-4 w-4 text-gray-500" />
                  )}
                </button>
              ) : (
                <span className="w-5" /> // Spacer for alignment
              )}
              <span className="font-mono text-sm font-medium text-brick-primary">
                RENO-{project.id}
              </span>
            </div>
          ),
        },
        // Title column
        {
          id: "title",
          content: (
            <div>
              <span className="font-medium text-gray-900 truncate block">
                {project.title}
              </span>
              {project.description && (
                <span className="text-xs text-gray-500 truncate block">
                  {project.description.substring(0, 60)}
                  {project.description.length > 60 ? "..." : ""}
                </span>
              )}
            </div>
          ),
        },
        // Building column
        {
          id: "building",
          content: project.building ? (
            <div className="flex items-center gap-1">
              <Buildings className="h-4 w-4 text-moss-primary flex-shrink-0" />
              <span className="text-sm truncate">{project.building.id}</span>
            </div>
          ) : (
            <span className="text-gray-400 text-sm">-</span>
          ),
        },
        // Company column
        {
          id: "company",
          content: (
            <span className="text-sm text-gray-600 truncate block">
              {project.building?.company?.name || "-"}
            </span>
          ),
        },
        // Status column
        {
          id: "status",
          content: (
            <span
              className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(project.status)}`}
            >
              {project.status}
            </span>
          ),
        },
        // Parts column
        {
          id: "parts",
          content: (
            <span className="text-sm text-gray-600">
              {project.parts?.length || 0}
            </span>
          ),
        },
        // Date column
        {
          id: "date",
          content: (
            <span className="text-sm text-gray-500">
              {new Date(project.createdAt).toLocaleDateString("nl-NL", {
                day: "numeric",
                month: "short",
                year: "numeric",
              })}
            </span>
          ),
        },
        // Actions column
        {
          id: "actions",
          content: (
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                handleProjectClick(project);
              }}
            >
              <Eye className="h-4 w-4" />
            </Button>
          ),
        },
      ];
    },
    [handleProjectClick, expandedRows, toggleRowExpansion],
  );

  // Calculate remaining items to load
  const remainingCount = totalCount ? totalCount - projects.length : 0;

  return (
    <>
      <div className="bg-brick-secondary-fine flex flex-col gap-0">
        {/* Header */}
        <header className="px-6 pt-6 pb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Wall className="h-8 w-8 text-brick-primary" weight="duotone" />
              <div>
                <h1 className="text-3xl font-bold text-gray-900">
                  Renovatieprojecten
                </h1>
                <p className="text-sm text-gray-500">
                  Overzicht van alle renovatieprojecten
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {/* Load All Button */}
              {hasMore && !isLoadingAll && (
                <Button
                  variant="outline"
                  onClick={handleLoadAll}
                  disabled={isLoading || isLoadingMore}
                >
                  <ArrowsClockwise className="h-4 w-4 mr-2" />
                  Laad alles ({remainingCount} resterend)
                </Button>
              )}

              {/* Cancel Load All Button */}
              {isLoadingAll && (
                <Button variant="outline" onClick={handleCancelLoadAll}>
                  <Spinner className="h-4 w-4 mr-2 animate-spin" />
                  Stoppen
                </Button>
              )}

              <Button
                onClick={() => setBuildingPickerOpen(true)}
                className="bg-moss-primary hover:bg-moss-primaryDark"
              >
                <Plus className="h-4 w-4 mr-2" />
                Nieuw project
              </Button>
            </div>
          </div>
        </header>

        {/* Statistics */}
        <div className="px-6 pt-4 pb-2">
          <div className="flex items-center gap-6">
            <div className="flex flex-col">
              <span className="text-neutral-primary text-2xl font-bold">
                {totalCount ?? "..."}
              </span>
              <span className="text-xs text-gray-500 uppercase">
                Totaal projecten
              </span>
            </div>

            <div className="h-10 w-px bg-gray-300"></div>

            <div className="ml-auto flex flex-col items-end">
              <span className="text-sm text-gray-600">
                Toont{" "}
                <span className="font-semibold">{projects.length}</span> /{" "}
                <span className="font-semibold">{totalCount ?? "..."}</span>
              </span>
              <span className="text-xs text-gray-500">Geladen projecten</span>
            </div>
          </div>
        </div>

        {/* Single UniversalTable with expandable rows */}
        <UniversalTable
          columnsConfig={RENOVATION_PROJECTS_TABLE_CONFIG.columnsConfig}
          sortConfig={RENOVATION_PROJECTS_TABLE_CONFIG.sortConfig}
          rows={projects}
          groupBy="none"
          title=""
          renderRow={renderRow}
          onRowClick={handleProjectClick}
          isLoading={isLoading}
          getRowKey={(row) => `project-${row.id}`}
          expandedRows={expandedRows}
          renderExpandedContent={renderExpandedContent}
        />

        {/* Load More Section - pb-28 ensures visibility above fixed FilterBar */}
        <div className="px-6 py-4 pb-28 border-t bg-white">
          {isLoadingMore || isLoadingAll ? (
            <div className="flex items-center justify-center gap-2 text-gray-500">
              <Spinner className="h-5 w-5 animate-spin" />
              <span>
                {isLoadingAll
                  ? `Alle projecten laden... (${projects.length} / ${totalCount ?? "..."})`
                  : "Meer projecten laden..."}
              </span>
            </div>
          ) : hasMore ? (
            <div className="flex flex-col items-center gap-2">
              <Button
                variant="outline"
                onClick={handleLoadMore}
                className="w-full max-w-md"
              >
                <ArrowDown className="h-4 w-4 mr-2" />
                Laad volgende 50 projecten ({remainingCount} resterend)
              </Button>
              <span className="text-xs text-gray-400">
                {projects.length} van {totalCount} projecten geladen
              </span>
            </div>
          ) : projects.length > 0 ? (
            <div className="text-center text-sm text-gray-500">
              Alle {totalCount ?? projects.length} projecten geladen
            </div>
          ) : null}
        </div>
      </div>

      {/* FilterBar */}
      <FilterBar
        searchValue={searchText}
        onSearchChange={setSearchText}
        searchPlaceholder="Zoek op titel, gebouw, RENO-nummer..."
        filterChips={filterChips}
        filterSections={filterSections}
        showWhenEmpty={true}
        showSpacer={false}
      />

      {/* Step 1 of "Nieuw project" — building picker (only here, since the
          list spans multiple buildings). On confirm we open the create
          modal pre-scoped to the chosen building. */}
      <UniversalPickerModal
        isOpen={buildingPickerOpen}
        mode="building"
        title="Selecteer gebouw voor het nieuwe project"
        onClose={() => setBuildingPickerOpen(false)}
        onConfirm={(value) => {
          setBuildingPickerOpen(false);
          if (value && typeof value === "object" && "id" in value) {
            setPickedBuildingId((value as { id: string }).id);
            setCreateModalOpen(true);
          }
        }}
      />

      {pickedBuildingId && (
        <NewRenovationProjectModal
          isOpen={createModalOpen}
          buildingId={pickedBuildingId}
          onClose={() => setCreateModalOpen(false)}
        />
      )}
    </>
  );
};

export const RenovationProjectsListPage = memo(
  RenovationProjectsListPageComponent,
);
